import { useState } from 'react';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { Stack, Alert, IconButton, InputAdornment, Link } from '@mui/material';
import { useAuthContext } from '@hooks/useAuthContext';
import { Button } from '@PorkyCol/button';

import { Iconic } from '@commons/iconic';
import FormProvider, { RHFTextField } from '@commons/hook-form';
import { errorAuth } from '@/utils/errors';
import { useMutation } from '@tanstack/react-query';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { me } from '@/services';
import { UserStore } from '@/store/user';
import { translates } from '@/utils';

type FormValuesProps = {
  email: string;
  password: string;
  afterSubmit?: string;
};

export default function AuthLoginForm() {
  const { setInitialized, init } = UserStore.getState();
  const { login } = useAuthContext();
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);

  const LoginSchema = Yup.object().shape({
    email: Yup.string().required('Email is required').email('Email must be a valid email address'),
    password: Yup.string().required('Password is required'),
  });

  const defaultValues = {
    email: '',
    password: '',
  };

  const methods = useForm<FormValuesProps>({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });

  const {
    reset,
    setError,
    handleSubmit,
    formState: { errors, isSubmitting, isSubmitSuccessful },
  } = methods;

  const onSubmit = async (data: FormValuesProps) => {
    try {
      const resolve = await login(data.email, data.password);
      if (resolve?.code) {
        console.error('error');
        reset();
        setError('afterSubmit', {
          ...resolve,
          message: errorAuth[resolve.code] ?? "Can't login. Please check your email and password.",
        });
      }
      const user = await me();
      setInitialized(true);
      await init(user);
    } catch (error: any) {
      console.error(error);
      reset();
      setError('afterSubmit', {
        ...error,
        message: errorAuth[error.code] ?? "Can't login. Please check your email and password.",
      });
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        {!!errors.afterSubmit && <Alert severity="error">{errors.afterSubmit.message}</Alert>}

        <RHFTextField name="email" label="Email address" />

        <RHFTextField
          name="password"
          label="Password"
          type={showPassword ? 'text' : 'password'}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                  <Iconic icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      <Stack alignItems="flex-end" sx={{ my: 2 }}>
        <Link component={RouterLink} to="auth/" variant="body2" color="inherit" underline="always">
          {translates['auth.login.forgotPassword']}
        </Link>
      </Stack>

      <Button
        fullWidth
        size="md"
        type="submit"
        loading={isSubmitSuccessful || isSubmitting}
        color="primary"
      >
        {translates['auth.login.loginText']}
      </Button>
    </FormProvider>
  );
}
